import type { HookCode, WorkflowJsonObject } from '@atlas/types'
import { HookStage } from '@atlas/types'
import { Button, cn, Input } from '@atlas/ui-primitives'
import { type KeyboardEvent, useState } from 'react'
import { Eyebrow } from '@/components/editorial/PageHeader'
import type { TransitionHookDTO } from '@/domain/workflow-admin.types'
import type { SequenceAgreement } from '@/domain/workflow-builder-layout'
import { type ChecklistOption, HookSection } from './TransitionHookSection'

/** Enter commits immediately, same as tabbing/clicking away (both blur the field). */
function commitOnEnter(e: KeyboardEvent<HTMLInputElement>) {
	if (e.key === 'Enter') e.currentTarget.blur()
}

export type Direction = 'forward' | 'backward'

export interface WorkflowTransitionInspectorProps {
	fromLabel: string
	toLabel: string
	dir: Direction
	hasForward: boolean
	hasBackward: boolean
	onSwitchDir: (dir: Direction) => void
	onAddBackward: () => void
	ctaLabel: string
	onChangeCta: (value: string) => void
	preHooks: readonly TransitionHookDTO[]
	postHooks: readonly TransitionHookDTO[]
	templates: readonly ChecklistOption[]
	isAttachingHook: boolean
	onAttachHook: (stage: HookStage, hookCode: HookCode) => void
	onRemoveHook: (hookId: string) => void
	onUpdateHookParams: (hookId: string, params: WorkflowJsonObject) => void
	onDeleteDirection: () => void
	/** Whether this connector still runs with the workflow's step order - see `sequenceAgreementOf`. */
	sequenceAgreement: SequenceAgreement
	onFixDirection: () => void
	isFixingDirection?: boolean
}

/** The forward/backward tab switcher, plus the reminder that each tab edits an independent transition row. */
function DirectionTabs({ dir, onSwitchDir }: { dir: Direction; onSwitchDir: (dir: Direction) => void }) {
	return (
		<>
			<div className="mb-1.5 flex gap-1.5 rounded-ds-full bg-ds-surface p-1">
				<button
					type="button"
					onClick={() => onSwitchDir('forward')}
					className={cn(
						'flex-1 rounded-ds-full py-1.5 text-xs',
						dir === 'forward' ? 'bg-ds-primary text-ds-background' : 'text-ds-text-muted',
					)}
				>
					Forward →
				</button>
				<button
					type="button"
					onClick={() => onSwitchDir('backward')}
					className={cn(
						'flex-1 rounded-ds-full py-1.5 text-xs',
						dir === 'backward' ? 'bg-ds-primary text-ds-background' : 'text-ds-text-muted',
					)}
				>
					← Backward
				</button>
			</div>
			<p className="mb-3 font-mono text-ds-4xs text-ds-text-subtle">
				Two independent transition records share this connector. You are editing {dir} only.
			</p>
		</>
	)
}

/** Shown in place of the CTA/hooks form when the active direction has no `workflow_transition` row yet. */
function NoBackwardYet({ onAddBackward }: { onAddBackward: () => void }) {
	return (
		<div className="rounded-ds-md border border-dashed border-ds-border bg-ds-surface p-4 text-center">
			<p className="mb-3 text-sm text-ds-text-muted">
				No backward transition exists on this connector. Adding one creates a separate record with its own CTA and
				hooks.
			</p>
			<Button onPress={onAddBackward}>＋ Add backward transition</Button>
		</div>
	)
}

/**
 * A stored direction the step order contradicts. It is reported rather than
 * corrected on the administrator's behalf: direction is configuration they
 * set, and a backward leg against the order can be deliberate. A tie leaves
 * the comparison undefined, so it points at the reorder control instead of
 * offering a flip that would be a guess.
 */
function SequenceMismatchNotice({
	agreement,
	dir,
	onFixDirection,
	isFixingDirection,
}: {
	agreement: SequenceAgreement
	dir: Direction
	onFixDirection: () => void
	isFixingDirection?: boolean
}) {
	if (agreement === 'agrees') return null
	const opposite = dir === 'forward' ? 'backward' : 'forward'
	return (
		<div className="rounded-ds-md border border-ds-warning bg-ds-surface p-3">
			{agreement === 'tied' ? (
				<p className="text-ds-2xs leading-relaxed text-ds-text-muted">
					These two statuses share a step position, so this transition's direction cannot be checked against the order.
					Reorder the workflow's statuses to separate them.
				</p>
			) : (
				<>
					<p className="mb-2 text-ds-2xs leading-relaxed text-ds-text-muted">
						This transition is stored as {dir}, but the step order runs the other way. The engine classifies moves on
						step order, so it is gated as a {opposite} move.
					</p>
					<Button size="sm" variant="secondary" onPress={onFixDirection} isDisabled={isFixingDirection}>
						Store it as {opposite}
					</Button>
				</>
			)}
		</div>
	)
}

/**
 * The builder's per-transition inspector. Forward and backward are two
 * independent `workflow_transition` rows sharing one connector — switching
 * the tab swaps which row's CTA/hooks this panel edits; it never merges them.
 */
export function WorkflowTransitionInspector({
	fromLabel,
	toLabel,
	dir,
	hasForward,
	hasBackward,
	onSwitchDir,
	onAddBackward,
	ctaLabel,
	onChangeCta,
	preHooks,
	postHooks,
	templates,
	isAttachingHook,
	onAttachHook,
	onRemoveHook,
	onUpdateHookParams,
	onDeleteDirection,
	sequenceAgreement,
	onFixDirection,
	isFixingDirection,
}: WorkflowTransitionInspectorProps) {
	const [openMenu, setOpenMenu] = useState<'pre' | 'post' | null>(null)
	// A local draft, committed on blur/Enter — a controlled input firing
	// onChangeCta per keystroke re-renders with the still-stale server value
	// before the mutation resolves, so typing appears to do nothing. The
	// parent remounts this component (via `key`) on transition/direction
	// switch, so the draft always starts fresh for whichever edge is open.
	const [cta, setCta] = useState(ctaLabel)
	const showingDirection = dir === 'forward' ? hasForward : hasBackward

	return (
		<div>
			<Eyebrow className="mb-1">Transition</Eyebrow>
			<div className="mb-3 text-ds-sm">
				{fromLabel} <span className="text-ds-text-subtle">→</span> {toLabel}
			</div>

			<DirectionTabs dir={dir} onSwitchDir={onSwitchDir} />

			{!showingDirection ? (
				<NoBackwardYet onAddBackward={onAddBackward} />
			) : (
				<div className="space-y-3">
					<SequenceMismatchNotice
						agreement={sequenceAgreement}
						dir={dir}
						onFixDirection={onFixDirection}
						isFixingDirection={isFixingDirection}
					/>
					<div>
						<label className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">
							CTA label
						</label>
						<Input
							aria-label="CTA label"
							value={cta}
							onChange={(e) => setCta(e.target.value)}
							onBlur={() => cta !== ctaLabel && onChangeCta(cta)}
							onKeyDown={commitOnEnter}
						/>
						<p className="mt-1 font-mono text-ds-4xs text-ds-text-subtle">Shown on the work order action bar.</p>
					</div>

					<HookSection
						stage="pre"
						hooks={preHooks}
						templates={templates}
						emptyHint="No guards — transition always available."
						menuOpen={openMenu === 'pre'}
						isAttaching={isAttachingHook}
						onToggleMenu={() => setOpenMenu(openMenu === 'pre' ? null : 'pre')}
						onAttach={(hookCode) => {
							onAttachHook(HookStage.PRE, hookCode)
							setOpenMenu(null)
						}}
						onRemove={onRemoveHook}
						onUpdateParams={onUpdateHookParams}
					/>

					<HookSection
						stage="post"
						hooks={postHooks}
						templates={templates}
						emptyHint="Nothing fires after this transition."
						menuOpen={openMenu === 'post'}
						isAttaching={isAttachingHook}
						onToggleMenu={() => setOpenMenu(openMenu === 'post' ? null : 'post')}
						onAttach={(hookCode) => {
							onAttachHook(HookStage.POST, hookCode)
							setOpenMenu(null)
						}}
						onRemove={onRemoveHook}
						onUpdateParams={onUpdateHookParams}
					/>

					<Button
						variant="muted"
						className="w-full rounded-ds-full border-ds-danger text-ds-danger"
						onPress={onDeleteDirection}
					>
						Delete this direction
					</Button>
				</div>
			)}
		</div>
	)
}
